import { Component, Prop, Watch } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import FieldWrapper from '../field-wrapper/field-wrapper.vue';
import AbstractField from '@/app/modules/shared/components/forms/inputs/abstract-field/abstract-field';
import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';
import 'quill/dist/quill.bubble.css';
import { quillEditor } from 'vue-quill-editor';
import { PrismEditor } from 'vue-prism-editor';
import 'vue-prism-editor/dist/prismeditor.min.css';
import { highlight, languages } from 'prismjs/components/prism-core';
import 'prismjs/components/prism-markup';
import 'prismjs/themes/prism-dark.css';
import ImageUploadModal from '@shared/components/modals/image-upload-modal/image-upload-modal.vue';
import DocUploadModal from '@shared/components/modals/doc-upload-modal/doc-upload-modal.vue';
import BpcTemplateVariablesHelp from '@tools/components/template-variables-help.vue';

import Quill from 'quill';
import { TemplateRelatedInLoop, TemplateVariable } from '@tools/interfaces/template';
const Inline = Quill.import('blots/inline');

// Custom extensions to format the editor
class TemplateSpanBlot extends Inline {
  static blotName = 'templatevar';
  static className = 'template-variable';
  static tagName = 'span';
  static create(value) {
    const node = super.create(value);
    return node;
  }

  static formats(domNode) {
    return true;
  }
}
class EmptySpanBlot extends Inline {
  static blotName = 'emptyspan';
  static className = 'empty-span';
  static tagName = 'span';
  static create(value) {
    const node = super.create(value);
    return node;
  }

  static formats(domNode) {
    return true;
  }
}
class LogicBlockSpanBlot extends Inline {
  static blotName = 'logicblock';
  static className = 'logicblock';
  static tagName = 'span';
  static create(value) {
    const node = super.create(value);
    return node;
  }

  static formats(domNode) {
    return true;
  }
}

Quill.register(TemplateSpanBlot);
Quill.register(LogicBlockSpanBlot);
Quill.register(EmptySpanBlot);

@Component({
  name: 'bpc-text-editor',
  components: {
    'bpc-field-wrapper': FieldWrapper,
    'bpc-image-upload-modal': ImageUploadModal,
    'bpc-doc-upload-modal': DocUploadModal,
    'quill-editor': quillEditor,
    'prism-editor': PrismEditor,
    BpcTemplateVariablesHelp,
  },
})
export default class TextEditor extends mixins(AbstractField) {
  $refs: {
    myQuillEditor: InstanceType<typeof quillEditor> & {
      quill: void;
    };
  };
  @Prop(String) readonly value: string;
  @Prop({ type: Boolean, default: true }) readonly codeEditor: boolean;
  @Prop({ type: Boolean, default: false })
  readonly withTemplateVariables: boolean;
  @Prop({ type: Boolean, default: false })
  readonly withMagicAI: boolean;

  public toggleText = true;
  public toggleCode = false;

  public showImageUploadModal: boolean = false;
  public imageLoadKey: number = 0;

  public showTemplateVariablesHelp: boolean = false;
  public templateVariablesKey: number = 0;

  public showDocUploadModal: boolean = false;
  public docLoadKey: number = 1;

  public hasChangedToUseInPdf: boolean = false;

  get model(): string {
    return this.value;
  }
  set model(val: string) {
    this.$emit('input', val);
    if (this.eventsConfig && this.eventsConfig.emit) {
      this.eventsConfig.emit.forEach(event => {
        this.$root.$emit(event, val);
      });
    }
  }

  get useInPdf() {
    return this.formModel?.type === 'pdf' || this.formModel?.type === 'attestation';
  }

  get editorPlaceholder(): string {
    return this.$t('global.message.editor.placeholder') as string;
  }
  get smallSizeLabel(): string {
    return this.$t('global.message.editor.size.small') as string;
  }
  get normalSizeLabel(): boolean {
    return false;
  }
  get largeSizeLabel(): string {
    return this.$t('global.message.editor.size.large') as string;
  }
  get hugeSizeLabel(): string {
    return this.$t('global.message.editor.size.huge') as string;
  }

  get editorOption() {
    const tools = ['link', 'image', 'video', 'doc'];
    // if (this.withTemplateVariables) {
    //   tools.push('template_variables');
    // }
    // if (this.withMagicAI) {
    //   tools.push('magic_ai');
    // }
    return {
      // Some Quill options...
      theme: 'snow',
      boundary: document.body,
      modules: {
        toolbar: {
          container: [
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            ['bold', 'italic', 'underline'], // toggled buttons - possible add 'strike'
            ['blockquote'], // 'code-block'
            [{ color: [] }, { background: [] }], // dropdown with defaults from theme
            // [{ 'header': 1 }, { 'header': 2 }],            // custom button values
            [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
            // [{ 'script': 'sub' }, { 'script': 'super' }],  // superscript/subscript
            // [{ 'indent': '-1' }, { 'indent': '+1' }],      // outdent/indent
            // [{ 'direction': 'rtl' }],                      // text direction
            // [{ size: ['small', false, 'large', 'huge'] }], // custom dropdown, // custom dropdown
            // [{ font: [] }],
            [{ align: [] }],
            ['clean'], // remove formatting button
            tools,
          ],
          handlers: {
            image: this.addImageButton,
            doc: this.addDocButton,
            // magic_ai: this.addMagicAIButton,
            // template_variables: this.addTemplateVariablesButton,
          },
        },
      },
      placeholder: this.editorPlaceholder,
      readOnly: false,
    };
  }

  changeToTextEditor() {
    this.toggleText = true;
    this.toggleCode = false;
  }

  changeToCodeEditor() {
    this.toggleText = false;
    this.toggleCode = true;
  }

  highlighter(model) {
    return highlight(model, languages.html);
  }

  addImageButton() {
    this.showImageUploadModal = true;
    this.imageLoadKey++;
  }

  addDocButton() {
    this.showDocUploadModal = true;
    this.docLoadKey++;
  }

  onCloseImageModal() {
    this.showImageUploadModal = false;
    this.imageLoadKey++;
  }

  onCloseDocModal() {
    this.showDocUploadModal = false;
    this.docLoadKey++;
  }

  onUploadImage($event) {
    try {
      if ($event) {
        const length = this.editor.getSelection(true).index;
        // Write a picture
        if (this.useInPdf) {
          this.editor.insertEmbed(length, 'image', $event.data.base64);
        } else {
          this.editor.insertEmbed(length, 'image', $event.data.url);
        }
        this.editor.setSelection(length + 1);

        this.showImageUploadModal = false;
        this.imageLoadKey++;
      }
    } catch (error) {
      console.log(error);
    }
  }

  onUploadDoc($event) {
    try {
      if ($event) {
        const length = this.editor.getSelection(true).index;
        this.editor.insertText(length, $event.data.fileName, 'link', $event.data.url);
        this.editor.setSelection(length + 1);
        this.showDocUploadModal = false;
        this.docLoadKey++;
      }
    } catch (error) {
      console.log(error);
    }
  }

  get editor() {
    return this.$refs.myQuillEditor.quill;
  }

  onInsertTemplateVariable(variable: TemplateVariable) {
    try {
      if (variable) {
        const selection = this.editor.getSelection(true);
        const startPos = selection ? selection.index : 0;

        const content = `${variable.contentValue} `; // NOTE we add a white space intentionally
        const contentLength = content.length - 1;
        const contentAndWhiteSpaceLength = contentLength + 1;

        this.editor.insertText(startPos, content);
        this.editor.formatText(startPos, contentLength, 'templatevar', true);
        this.editor.setSelection(selection + contentAndWhiteSpaceLength);

        // this.showTemplateVariablesModal = false;
        // this.templateVariablesKey++;
      }
    } catch (error) {
      console.log(error);
    }
  }

  onInsertRelatedInLoop(variable: TemplateRelatedInLoop) {
    try {
      if (variable) {
        const selection = this.editor.getSelection(true);
        const startPos = selection ? selection.index : 0;
        const blockStartContent = `${variable.blockStartValue} \n`; // NOTE we add a white space intentionally
        const startSpanLength = blockStartContent.length - 2;

        const blockContent = `${variable.contentValue} \n`; // NOTE we add a white space intentionally
        const contentPos = startPos + startSpanLength + 2;
        const contentSpanLength = blockContent.length - 2;

        const blockEndContent = `${variable.blockEndValue} \n`; // NOTE we add a white space intentionally
        const endPos = contentPos + contentSpanLength + 2;
        const endSpanLength = blockEndContent.length - 2;

        this.editor.insertText(startPos, `${blockStartContent}${blockContent}${blockEndContent}`);

        this.editor.formatText(startPos, startSpanLength, 'logicblock', true);
        this.editor.formatText(contentPos, contentSpanLength, 'emptyspan', true);
        this.editor.formatText(endPos, endSpanLength, 'logicblock', true);

        this.editor.setSelection(contentPos + contentSpanLength + 1);

        // this.showTemplateVariablesModal = false;
        // this.templateVariablesKey++;
      }
    } catch (error) {
      console.log(error);
    }
  }

  addMagicAIButton() {
    // TODO put the magic here
  }

  addTemplateVariablesButton() {
    this.$emit('input-custom-event', { eventName: 'open_template_variables' });
    this.showTemplateVariablesHelp = true;
    this.templateVariablesKey++;
  }

  onCloseTemplateVariablesHelp() {
    this.$emit('input-custom-event', { eventName: 'close_template_variables' });
    this.showTemplateVariablesHelp = false;
    this.templateVariablesKey++;
  }

  toggleTemplateHelper() {
    if (this.showTemplateVariablesHelp) {
      this.onCloseTemplateVariablesHelp();
    } else {
      this.addTemplateVariablesButton();
    }
  }

  // created () { }

  mounted() {
    this.onCloseTemplateVariablesHelp();
  }

  // updated () { }

  destroyed() {
    this.onCloseTemplateVariablesHelp();
  }

  @Watch('useInPdf')
  onChangeUseInPdf(newValue, oldValue) {
    this.hasChangedToUseInPdf = !oldValue && newValue && !!this.model;
  }
}
